// Holiday header resolver for the Date/Time banner's {parsha} slot.
//
// Implements the shul's 4-stage header life-cycle. All decisions are made on
// the SCREEN's civil day (org timezone), with the Jewish day turning over in
// the evening exactly like the rest of the display:
//
//   STAGE 1  Standard weekday          → '' (caller keeps showing the parsha)
//   STAGE 2  Erev Yom Tov              → "ערב פסח"        (noon → candle lighting)
//   STAGE 3  Active holiday            → "יום א׳ דפסח"    (nightfall → nightfall)
//   STAGE 4  Motzei Yom Tov / Purim    → "מוצאי פסח"      (nightfall → midnight)
//
// Evening turnover ("flip") of the Jewish day:
//   • into a Yom Tov / Shabbos from a plain day — candle lighting (sunset − 18m),
//     matching computeIsShabbos so the banner never disagrees with the Shabbos-
//     gated elements sharing the screen;
//   • out of / between holy days (YT day 1 → day 2, Shabbos → YT) — tzais,
//     because the outgoing kodesh only ends at nightfall.
//
// Baked-in business rules (see the spec):
//   • Ta'anis Esther shows only from 4:00 AM (parsha before that) until tzais,
//     when it hands over to פורים for the night Megillah reading.
//   • Purim / Chanukah / Purim Katan / Shushan Purim never get an "ערב" state.
//   • Tisha B'Av and all other fast days are excluded entirely (parsha shows).
//   • 21 Tishrei shows הושענא רבא from the previous nightfall, never
//     "ערב שמיני עצרת" (the active-holiday check outranks the erev check).
//   • Motzei applies only to biblical Yom Tov and Purim, and is suppressed when
//     the night is itself holy (YT → YT, YT/Purim → Shabbos, YT → Chol Hamoed).
//   • Tu B'Shvat, Lag B'Omer, Pesach Sheni, Isru Chag, modern holidays etc.
//     never replace the parsha (strict allowlist).
//
// The resolver is dependency-injected (the caller hands over the already-loaded
// kosher-zmanim module) so this file stays pure logic: no imports, trivially
// unit-testable, and it cannot bloat the entry bundle. It owns a private
// ComplexZmanimCalendar — it never mutates the display's shared calendar.
//
// Every public entry point is wrapped defensively: on ANY unexpected failure it
// returns '' and the banner simply keeps showing the weekly parsha.

const MINUTES_NOON = 12 * 60;
const FAST_START_MINUTES = 4 * 60; // Ta'anis Esther appears strictly from 4:00 AM
const MS_PER_MINUTE = 60000;
const DEFAULT_CANDLE_OFFSET_MINUTES = 18;
// When getTzais() itself is unavailable, nightfall falls back to sunset + 50m
// (the same magnitude as the display's tzeit50min fallback chain).
const FALLBACK_TZAIS_OFFSET_MINUTES = 50;

// Day-of-Yom-Tov numerals (1..8) for "יום א׳ דפסח" style labels.
const HEBREW_DAY_NUMERALS = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ז׳', 'ח׳'];

const WEEKDAY_MAP = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

// ---------------------------------------------------------------- utilities

function toValidDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

// kosher-zmanim getters return a Luxon DateTime (or null). Normalize to epoch
// ms; also accepts a plain Date for safety.
function zmanToMs(value) {
  if (!value) return null;
  try {
    if (typeof value.toMillis === 'function') {
      const ms = value.toMillis();
      return Number.isFinite(ms) ? ms : null;
    }
    if (typeof value.toJSDate === 'function') {
      const d = value.toJSDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : null;
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.getTime();
    }
  } catch (error) {
    return null;
  }
  return null;
}

export function createHolidayHeaderResolver(options = {}) {
  const {
    kosherZmanim,
    latitude,
    longitude,
    timezone,
    inIsrael = false,
    candleOffsetMinutes = DEFAULT_CANDLE_OFFSET_MINUTES,
  } = options;

  const JewishCalendar = kosherZmanim && kosherZmanim.JewishCalendar;
  if (typeof JewishCalendar !== 'function') return null;

  const JC = JewishCalendar; // holiday-index constants live on the class

  // ---- private zmanim calendar (never the display's shared one) ----------
  let zmanimCalendar = null;
  try {
    const { ComplexZmanimCalendar, GeoLocation } = kosherZmanim;
    if (
      typeof ComplexZmanimCalendar === 'function' &&
      typeof GeoLocation === 'function' &&
      Number.isFinite(Number(latitude)) &&
      Number.isFinite(Number(longitude))
    ) {
      const zone =
        (typeof timezone === 'string' && timezone.trim()) ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'UTC';
      zmanimCalendar = new ComplexZmanimCalendar(
        new GeoLocation('holiday-header', Number(latitude), Number(longitude), 0, zone)
      );
    }
  } catch (error) {
    zmanimCalendar = null; // degrade: civil-day boundaries only (see dayInfo)
  }

  // ---- civil date parts in the screen's timezone -------------------------
  let partsFormatter = null;
  try {
    partsFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: (typeof timezone === 'string' && timezone.trim()) || undefined,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
  } catch (error) {
    partsFormatter = null; // invalid tz string — fall back to device-local below
  }

  function getCivilParts(now) {
    const fallback = {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
      dayIndex: now.getDay(),
      minutesOfDay: now.getHours() * 60 + now.getMinutes(),
    };
    if (!partsFormatter) return fallback;
    try {
      const map = {};
      partsFormatter.formatToParts(now).forEach((part) => {
        map[part.type] = part.value;
      });
      const year = Number(map.year);
      const month = Number(map.month);
      const day = Number(map.day);
      const hours = Number(map.hour);
      const minutes = Number(map.minute);
      const dayIndex = WEEKDAY_MAP[map.weekday];
      if (![year, month, day, hours, minutes, dayIndex].every(Number.isFinite)) return fallback;
      return { year, month, day, dayIndex, minutesOfDay: hours * 60 + minutes };
    } catch (error) {
      return fallback;
    }
  }

  // ---- per-civil-date info, memoized -------------------------------------
  // Values are pure functions of the civil date, so a tiny keyed cache keeps
  // the once-a-second banner render cheap.
  const dayInfoCache = new Map();

  function holidayIndexSafe(jc) {
    try {
      const index = jc.getYomTovIndex();
      return Number.isFinite(index) ? index : -1;
    } catch (error) {
      return -1;
    }
  }

  // Strict allowlist — anything not named here shows the regular parsha.
  function computeActiveLabel(index, jewishDayOfMonth) {
    switch (index) {
      case JC.PESACH: {
        // Nisan 15..22 → days 1..8 (in Israel only 15 and 21 carry this index).
        const dayNumber = jewishDayOfMonth - 14;
        const numeral = HEBREW_DAY_NUMERALS[dayNumber - 1];
        return numeral ? `יום ${numeral} דפסח` : 'פסח';
      }
      case JC.CHOL_HAMOED_PESACH:
        return 'חול המועד פסח';
      case JC.SHAVUOS: {
        if (inIsrael) return 'שבועות';
        // Sivan 6 → day 1, Sivan 7 → day 2.
        const dayNumber = jewishDayOfMonth - 5;
        const numeral = HEBREW_DAY_NUMERALS[dayNumber - 1];
        return numeral ? `יום ${numeral} דשבועות` : 'שבועות';
      }
      case JC.ROSH_HASHANA:
        return 'ראש השנה';
      case JC.YOM_KIPPUR:
        return 'יום כיפור';
      case JC.SUCCOS: {
        // Tishrei 15..21 → days 1..7 (YT index covers 15–16 outside Israel).
        const dayNumber = jewishDayOfMonth - 14;
        const numeral = HEBREW_DAY_NUMERALS[dayNumber - 1];
        return numeral ? `יום ${numeral} דסוכות` : 'סוכות';
      }
      case JC.CHOL_HAMOED_SUCCOS:
        return 'חול המועד סוכות';
      case JC.HOSHANA_RABBA:
        return 'הושענא רבא';
      case JC.SHEMINI_ATZERES:
        return 'שמיני עצרת';
      case JC.SIMCHAS_TORAH:
        return 'שמחת תורה';
      case JC.CHANUKAH:
        return 'חנוכה';
      case JC.PURIM:
        return 'פורים';
      case JC.SHUSHAN_PURIM:
        return 'שושן פורים';
      case JC.PURIM_KATAN:
        return 'פורים קטן';
      default:
        return '';
    }
  }

  // Holidays that earn a "מוצאי X" tail (nightfall → midnight): biblical Yom
  // Tov and Purim only — never Chanukah, Shushan Purim, Purim Katan or fasts.
  function computeMotzeiBase(index) {
    switch (index) {
      case JC.PESACH:
        return 'פסח';
      case JC.SHAVUOS:
        return 'שבועות';
      case JC.ROSH_HASHANA:
        return 'ראש השנה';
      case JC.YOM_KIPPUR:
        return 'יום כיפור';
      case JC.SUCCOS:
        return 'סוכות';
      case JC.SHEMINI_ATZERES:
        return 'שמיני עצרת';
      case JC.SIMCHAS_TORAH:
        return 'שמחת תורה';
      case JC.PURIM:
        return 'פורים';
      default:
        return '';
    }
  }

  // "ערב X" (noon → candle lighting) exists only for biblical Yom Tov.
  // Shemini Atzeres is listed for completeness but is unreachable in practice:
  // its erev (21 Tishrei) is Hoshana Rabba, whose active label wins the day.
  function computeErevBase(index) {
    switch (index) {
      case JC.PESACH:
        return 'פסח';
      case JC.SHAVUOS:
        return 'שבועות';
      case JC.ROSH_HASHANA:
        return 'ראש השנה';
      case JC.YOM_KIPPUR:
        return 'יום כיפור';
      case JC.SUCCOS:
        return 'סוכות';
      case JC.SHEMINI_ATZERES:
        return 'שמיני עצרת';
      default:
        return '';
    }
  }

  function buildDayInfo(year, month, day) {
    // Device-local noon of the civil Y/M/D: both kosher-zmanim calendars read
    // a JS Date's local Y/M/D, so this pins them to the intended date with no
    // DST / midnight boundary ambiguity, whatever timezone the device runs in.
    const localNoon = new Date(year, month - 1, day, 12, 0, 0, 0);

    const info = {
      dayIndex: localNoon.getDay(),
      yomTovIndex: -1,
      activeLabel: '',
      motzeiBase: '',
      erevBase: '',
      isTaanisEsther: false,
      isAssurYomTov: false,
      isCholHamoed: false,
      candleMs: null,
      tzaisMs: null,
    };

    try {
      const jc = new JewishCalendar(localNoon);
      if (typeof jc.setInIsrael === 'function') jc.setInIsrael(Boolean(inIsrael));

      const index = holidayIndexSafe(jc);
      info.yomTovIndex = index;
      info.isTaanisEsther = index === JC.FAST_OF_ESTHER;
      info.activeLabel = computeActiveLabel(index, jc.getJewishDayOfMonth());
      info.motzeiBase = computeMotzeiBase(index);
      info.erevBase = computeErevBase(index);
      try {
        info.isAssurYomTov = Boolean(jc.isYomTovAssurBemelacha());
      } catch (error) { /* keep false */ }
      try {
        info.isCholHamoed = Boolean(jc.isCholHamoed());
      } catch (error) { /* keep false */ }
    } catch (error) {
      // Calendar failure — behave like a plain weekday (parsha shows).
    }

    if (zmanimCalendar) {
      try {
        zmanimCalendar.setDate(localNoon);
        const sunsetMs =
          zmanToMs(zmanimCalendar.getSunset()) ||
          zmanToMs(zmanimCalendar.getSeaLevelSunset());
        const tzaisMs = zmanToMs(zmanimCalendar.getTzais());
        const candleOffset = Number.isFinite(Number(candleOffsetMinutes))
          ? Number(candleOffsetMinutes)
          : DEFAULT_CANDLE_OFFSET_MINUTES;
        if (sunsetMs != null) {
          info.candleMs = sunsetMs - candleOffset * MS_PER_MINUTE;
        }
        info.tzaisMs =
          tzaisMs != null
            ? tzaisMs
            : sunsetMs != null
              ? sunsetMs + FALLBACK_TZAIS_OFFSET_MINUTES * MS_PER_MINUTE
              : null;
        // Guard against degenerate high-latitude output: nightfall must never
        // precede candle lighting.
        if (info.candleMs != null && info.tzaisMs != null && info.tzaisMs < info.candleMs) {
          info.tzaisMs = info.candleMs;
        }
      } catch (error) {
        info.candleMs = null;
        info.tzaisMs = null;
      }
    }

    return info;
  }

  function getDayInfo(year, month, day) {
    const key = `${year}-${month}-${day}`;
    const cached = dayInfoCache.get(key);
    if (cached) return cached;
    const info = buildDayInfo(year, month, day);
    dayInfoCache.set(key, info);
    if (dayInfoCache.size > 12) {
      // Drop the oldest entry; the cache only ever needs today ± a day.
      dayInfoCache.delete(dayInfoCache.keys().next().value);
    }
    return info;
  }

  function getNextCivilDate(year, month, day) {
    const next = new Date(year, month - 1, day + 1, 12, 0, 0, 0);
    return { year: next.getFullYear(), month: next.getMonth() + 1, day: next.getDate() };
  }

  // Label carried by the incoming (post-flip) Jewish day. Ta'anis Esther is
  // gated: on its own eve it must NOT appear — parsha shows until 4:00 AM.
  function effectiveDayLabel(dayInfo) {
    if (dayInfo.isTaanisEsther) return '';
    return dayInfo.activeLabel;
  }

  function resolve(now) {
    const parts = getCivilParts(now);
    const today = getDayInfo(parts.year, parts.month, parts.day);
    const nextDate = getNextCivilDate(parts.year, parts.month, parts.day);
    const tomorrow = getDayInfo(nextDate.year, nextDate.month, nextDate.day);

    const nowMs = now.getTime();
    const todayIsKodesh = today.isAssurYomTov || today.dayIndex === 6;
    const tomorrowIsKodesh = tomorrow.isAssurYomTov || tomorrow.dayIndex === 6;

    // The evening flip of the Jewish day (see the header comment). When zmanim
    // are unavailable, flipMs stays null and the day only turns at midnight.
    const candleFlip = tomorrowIsKodesh && !todayIsKodesh;
    const flipMs = (candleFlip ? today.candleMs : today.tzaisMs) ?? today.tzaisMs;
    const afterNightfall = today.tzaisMs != null && nowMs >= today.tzaisMs;
    const afterFlip = flipMs != null && nowMs >= flipMs;

    // STAGE 4 — "מוצאי X" from the holiday's nightfall until midnight.
    // Suppressed when the night itself is holy: into Shabbos (spec rule), into
    // another Yom Tov day (day 1 → day 2 keeps the active label), or into Chol
    // Hamoed (the festival continues). Purim → Shushan Purim is deliberately
    // NOT suppressed: the night reads "מוצאי פורים", and שושן פורים takes over
    // from midnight.
    if (afterNightfall && today.motzeiBase) {
      const suppressed =
        tomorrow.dayIndex === 6 || tomorrow.isAssurYomTov || tomorrow.isCholHamoed;
      if (!suppressed) return `מוצאי ${today.motzeiBase}`;
    }

    // STAGE 3 (evening side) — after the flip the header belongs to the
    // incoming Jewish day: first night of Yom Tov from candle lighting, YT day
    // 2 / Motzei-Shabbos-into-YT from tzais, Purim & Chanukah nights from
    // tzais, Hoshana Rabba "from evening", etc.
    if (afterFlip) return effectiveDayLabel(tomorrow);

    // STAGE 3 (day side) — an active holiday owns its whole civil day.
    // Ta'anis Esther is the one fast that displays, strictly from 4:00 AM.
    if (today.isTaanisEsther) {
      return parts.minutesOfDay >= FAST_START_MINUTES ? 'תענית אסתר' : '';
    }
    if (today.activeLabel) return today.activeLabel;

    // STAGE 2 — "ערב X" from noon until the flip into the Yom Tov.
    if (parts.minutesOfDay >= MINUTES_NOON && tomorrow.erevBase) {
      return `ערב ${tomorrow.erevBase}`;
    }

    // STAGE 1 — plain day: caller shows the weekly parsha.
    return '';
  }

  return {
    // '' means "no holiday override — show the regular parsha".
    getHeaderLabel(now) {
      try {
        const date = toValidDate(now) || new Date();
        const label = resolve(date);
        return typeof label === 'string' ? label : '';
      } catch (error) {
        return '';
      }
    },
  };
}
